import { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { cn } from '../../lib/utils';
import { 
  Plus, Play, Trash2, Check, X, Clock, 
  ChevronDown, ChevronUp, Loader2, Edit,
  CheckCircle, XCircle, AlertCircle, Upload, FileJson
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  getTestCases, createTestCase, updateTestCase, deleteTestCase, 
  runTestCase, runPrompt, previewLangfuseImport, importLangfuseTrace
} from '../../lib/api';

const ASSERTION_TYPES = [
  { value: 'contains', label: 'Contains' },
  { value: 'not_contains', label: 'Does Not Contain' },
  { value: 'starts_with', label: 'Starts With' },
  { value: 'ends_with', label: 'Ends With' },
  { value: 'regex', label: 'Matches Regex' },
  { value: 'length_min', label: 'Min Length' },
  { value: 'length_max', label: 'Max Length' },
];

const EVALUATION_METHODS = [
  { value: 'exact_match', label: 'Exact Match', description: 'Output must match exactly' },
  { value: 'fuzzy_match', label: 'Fuzzy Match', description: 'Similarity-based comparison' },
  { value: 'llm_semantic', label: 'LLM Semantic', description: 'AI-powered semantic evaluation' },
];

export default function TestCasePanel({ prompt, content, variables, availableModels, useCurrentContent = false, onImportComplete }) {
  const [testCases, setTestCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [editingTestCase, setEditingTestCase] = useState(null);
  const [newTestCase, setNewTestCase] = useState({ 
    name: '', 
    inputs: [], 
    assertions: [],
    expected_output: '',
    evaluation_method: null
  });
  const [runningTest, setRunningTest] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [expandedResults, setExpandedResults] = useState({});
  const [selectedModel, setSelectedModel] = useState({ provider: 'openai', model: 'gpt-4o' });

  // Quick test state
  const [quickTestVars, setQuickTestVars] = useState({});
  const [quickTestResult, setQuickTestResult] = useState(null);
  const [runningQuickTest, setRunningQuickTest] = useState(false);

  // Langfuse import state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (prompt?.id) {
      loadTestCases();
    }
  }, [prompt?.id]);

  // Initialize quick test vars when variables change
  useEffect(() => {
    const vars = {};
    variables.forEach(v => {
      vars[v] = quickTestVars[v] || '';
    });
    setQuickTestVars(vars);
  }, [variables]);

  const loadTestCases = async () => {
    setLoading(true);
    try {
      const res = await getTestCases(prompt.id);
      setTestCases(res.data);
    } catch (err) {
      console.error('Failed to load test cases:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTestCase = async () => {
    if (!newTestCase.name) {
      toast.error('Please enter a test case name');
      return;
    }
    
    try {
      const testCaseData = {
        prompt_id: prompt.id,
        name: newTestCase.name,
        inputs: newTestCase.inputs,
        assertions: newTestCase.assertions,
        expected_output: newTestCase.expected_output || null,
        evaluation_method: newTestCase.evaluation_method
      };
      
      if (editingTestCase) {
        await updateTestCase(editingTestCase.id, testCaseData);
        toast.success('Test case updated');
      } else {
        await createTestCase(testCaseData);
        toast.success('Test case created');
      }
      
      setShowNewForm(false);
      setEditingTestCase(null);
      setNewTestCase({ 
        name: '', 
        inputs: [], 
        assertions: [],
        expected_output: '',
        evaluation_method: null
      });
      loadTestCases();
    } catch (err) {
      toast.error(editingTestCase ? 'Failed to update test case' : 'Failed to create test case');
    }
  };

  const handleEditTestCase = (tc) => {
    setEditingTestCase(tc);
    setNewTestCase({
      name: tc.name,
      inputs: tc.inputs || [],
      assertions: tc.assertions || [],
      expected_output: tc.expected_output || '',
      evaluation_method: tc.evaluation_method || null
    });
    setShowNewForm(true);
  };

  const handleDeleteTestCase = async (id) => {
    try {
      await deleteTestCase(id);
      toast.success('Test case deleted');
      loadTestCases();
    } catch (err) {
      toast.error('Failed to delete test case');
    }
  };

  const handleRunTestCase = async (testCase) => {
    setRunningTest(testCase.id);
    try {
      // Use current unsaved content if useCurrentContent is true
      const promptContent = useCurrentContent ? content : null;
      const res = await runTestCase(testCase.id, selectedModel.provider, selectedModel.model, promptContent);
      setTestResults(prev => ({ ...prev, [testCase.id]: res.data }));
      setExpandedResults(prev => ({ ...prev, [testCase.id]: true }));
      if (res.data.success) {
        if (res.data.all_passed) {
          toast.success('All assertions passed!');
        } else {
          toast.warning('Some assertions failed');
        }
      } else {
        toast.error(res.data.error || 'Test failed');
      }
    } catch (err) {
      toast.error('Failed to run test case');
    } finally {
      setRunningTest(null);
    }
  };

  const handleQuickTest = async () => {
    setRunningQuickTest(true);
    setQuickTestResult(null);
    try {
      const res = await runPrompt({
        prompt_content: content,
        variables: quickTestVars,
        provider: selectedModel.provider,
        model_name: selectedModel.model
      });
      setQuickTestResult(res.data);
      if (res.data.success) {
        toast.success('Prompt executed successfully');
      } else {
        toast.error(res.data.error || 'Execution failed');
      }
    } catch (err) {
      toast.error('Failed to run prompt');
    } finally {
      setRunningQuickTest(false);
    }
  };

  const addInputToNewTestCase = () => {
    if (variables.length === 0) return;
    const unusedVar = variables.find(v => !newTestCase.inputs.find(i => i.variable === v));
    if (unusedVar) {
      setNewTestCase(prev => ({
        ...prev,
        inputs: [...prev.inputs, { variable: unusedVar, value: '' }]
      }));
    }
  };

  const addAssertionToNewTestCase = () => {
    setNewTestCase(prev => ({
      ...prev,
      assertions: [...prev.assertions, { type: 'contains', value: '' }]
    }));
  };

  const updateInput = (index, field, value) => {
    setNewTestCase(prev => ({
      ...prev,
      inputs: prev.inputs.map((inp, i) => 
        i === index ? { ...inp, [field]: value } : inp
      )
    }));
  };

  const updateAssertion = (index, field, value) => {
    setNewTestCase(prev => ({
      ...prev,
      assertions: prev.assertions.map((a, i) => 
        i === index ? { ...a, [field]: value } : a
      )
    }));
  };

  const removeInput = (index) => {
    setNewTestCase(prev => ({
      ...prev,
      inputs: prev.inputs.filter((_, i) => i !== index)
    }));
  };

  const removeAssertion = (index) => {
    setNewTestCase(prev => ({
      ...prev,
      assertions: prev.assertions.filter((_, i) => i !== index)
    }));
  };

  // Langfuse import handlers
  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      toast.error('Please select a JSON file');
      return;
    }

    setPreviewing(true);
    try {
      const text = await file.text();
      const traceData = JSON.parse(text);
      
      const res = await previewLangfuseImport(traceData);
      if (res.data.success) {
        setImportPreview({ ...res.data.preview, rawData: traceData });
        setShowImportDialog(true);
      } else {
        toast.error('Failed to parse Langfuse trace');
      }
    } catch (err) {
      toast.error(`Failed to read file: ${err.message}`);
    } finally {
      setPreviewing(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImport = async () => {
    if (!importPreview?.rawData) return;

    setImporting(true);
    try {
      const res = await importLangfuseTrace(importPreview.rawData);
      if (res.data.success) {
        toast.success(`Imported: ${res.data.imported.agents} agents, ${res.data.imported.tasks} tasks, ${res.data.imported.prompts} prompts, ${res.data.imported.test_cases} test cases`);
        setShowImportDialog(false);
        setImportPreview(null);
        // Notify parent to refresh the data
        onImportComplete?.();
        loadTestCases();
      }
    } catch (err) {
      toast.error(`Import failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="h-full overflow-auto p-4 space-y-6">
        {/* Test Cases */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-[#F1F5F9]">Test Cases</h3>
            <div className="flex items-center gap-2">
              {/* Langfuse Import Button */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".json"
                className="hidden"
                data-testid="langfuse-file-input"
              />
              <Button 
                size="sm" 
                variant="outline" 
                className="border-[#1E293B]"
                onClick={() => fileInputRef.current?.click()}
                disabled={previewing}
                data-testid="import-langfuse-btn"
              >
                {previewing ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <FileJson className="w-4 h-4 mr-1" />
                )}
                Import Langfuse
              </Button>

              {/* Add Test Case Button */}
              <Dialog open={showNewForm} onOpenChange={(open) => {
                setShowNewForm(open);
                if (!open) {
                  setEditingTestCase(null);
                  setNewTestCase({ 
                    name: '', 
                    inputs: [], 
                    assertions: [],
                    expected_output: '',
                    evaluation_method: null
                  });
                }
              }}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" className="border-[#1E293B]">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Test Case
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0F172A] border-[#1E293B]">
                <DialogHeader>
                  <DialogTitle className="text-[#F1F5F9]">
                    {editingTestCase ? 'Edit Test Case' : 'Create Test Case'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-[#94A3B8]">Test Name</Label>
                    <Input
                      value={newTestCase.name}
                      onChange={(e) => setNewTestCase(prev => ({ ...prev, name: e.target.value }))}
                      className="bg-[#030712] border-[#1E293B]"
                      placeholder="e.g., Happy path test"
                    />
                  </div>

                  {/* Inputs */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-[#94A3B8]">Input Values</Label>
                      <Button size="sm" variant="ghost" onClick={addInputToNewTestCase}>
                        <Plus className="w-3 h-3 mr-1" />
                        Add
                      </Button>
                    </div>
                    {newTestCase.inputs.map((inp, i) => (
                      <div key={i} className="flex gap-2">
                        <Select
                          value={inp.variable}
                          onValueChange={(v) => updateInput(i, 'variable', v)}
                        >
                          <SelectTrigger className="w-32 bg-[#030712] border-[#1E293B]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0F172A] border-[#1E293B]">
                            {variables.map(v => (
                              <SelectItem key={v} value={v}>{`{${v}}`}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          value={inp.value}
                          onChange={(e) => updateInput(i, 'value', e.target.value)}
                          className="flex-1 bg-[#030712] border-[#1E293B]"
                          placeholder="Value..."
                        />
                        <Button size="icon" variant="ghost" onClick={() => removeInput(i)}>
                          <X className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  {/* Expected Output */}
                  <div className="space-y-2">
                    <Label className="text-xs text-[#94A3B8]">Expected Output (Optional)</Label>
                    <Textarea
                      value={newTestCase.expected_output}
                      onChange={(e) => setNewTestCase(prev => ({ ...prev, expected_output: e.target.value }))}
                      className="bg-[#030712] border-[#1E293B] min-h-[100px] font-mono text-sm"
                      placeholder="Enter expected output for comparison..."
                    />
                    <p className="text-[10px] text-[#64748B]">
                      If provided, the actual output will be compared against this expected output.
                    </p>
                  </div>

                  {/* Evaluation Method - only show if expected output is provided */}
                  {newTestCase.expected_output && (
                    <div className="space-y-2">
                      <Label className="text-xs text-[#94A3B8]">Evaluation Method</Label>
                      <Select
                        value={newTestCase.evaluation_method?.type || ''}
                        onValueChange={(v) => setNewTestCase(prev => ({ 
                          ...prev, 
                          evaluation_method: v ? { type: v, threshold: 0.8 } : null
                        }))}
                      >
                        <SelectTrigger className="bg-[#030712] border-[#1E293B]">
                          <SelectValue placeholder="Select evaluation method..." />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0F172A] border-[#1E293B]">
                          {EVALUATION_METHODS.map(m => (
                            <SelectItem key={m.value} value={m.value}>
                              <div className="flex flex-col">
                                <span>{m.label}</span>
                                <span className="text-[10px] text-[#64748B]">{m.description}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      {/* Threshold slider for fuzzy match */}
                      {newTestCase.evaluation_method?.type === 'fuzzy_match' && (
                        <div className="space-y-1 mt-2">
                          <div className="flex justify-between">
                            <Label className="text-[10px] text-[#64748B]">Similarity Threshold</Label>
                            <span className="text-[10px] text-[#0EA5E9] font-mono">
                              {Math.round((newTestCase.evaluation_method?.threshold || 0.8) * 100)}%
                            </span>
                          </div>
                          <input
                            type="range"
                            min="0.5"
                            max="1"
                            step="0.05"
                            value={newTestCase.evaluation_method?.threshold || 0.8}
                            onChange={(e) => setNewTestCase(prev => ({
                              ...prev,
                              evaluation_method: {
                                ...prev.evaluation_method,
                                threshold: parseFloat(e.target.value)
                              }
                            }))}
                            className="w-full h-1 bg-[#1E293B] rounded-lg appearance-none cursor-pointer"
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Assertions */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-[#94A3B8]">Assertions (Optional)</Label>
                      <Button size="sm" variant="ghost" onClick={addAssertionToNewTestCase}>
                        <Plus className="w-3 h-3 mr-1" />
                        Add
                      </Button>
                    </div>
                    {newTestCase.assertions.map((a, i) => (
                      <div key={i} className="flex gap-2">
                        <Select
                          value={a.type}
                          onValueChange={(v) => updateAssertion(i, 'type', v)}
                        >
                          <SelectTrigger className="w-36 bg-[#030712] border-[#1E293B]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0F172A] border-[#1E293B]">
                            {ASSERTION_TYPES.map(t => (
                              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          value={a.value}
                          onChange={(e) => updateAssertion(i, 'value', e.target.value)}
                          className="flex-1 bg-[#030712] border-[#1E293B]"
                          placeholder="Expected value..."
                        />
                        <Button size="icon" variant="ghost" onClick={() => removeAssertion(i)}>
                          <X className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => {
                    setShowNewForm(false);
                    setEditingTestCase(null);
                  }}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateTestCase} className="bg-[#0EA5E9] hover:bg-[#0284C7]">
                    {editingTestCase ? 'Update' : 'Create'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            </div>
          </div>

          {/* Langfuse Import Dialog */}
          <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-[#0F172A] border-[#1E293B]">
              <DialogHeader>
                <DialogTitle className="text-[#F1F5F9] flex items-center gap-2">
                  <FileJson className="w-5 h-5 text-[#0EA5E9]" />
                  Import Langfuse Trace
                </DialogTitle>
                <DialogDescription className="text-[#64748B]">
                  Preview what will be imported from the Langfuse trace file
                </DialogDescription>
              </DialogHeader>

              {importPreview && (
                <div className="space-y-4 py-4">
                  {/* Summary */}
                  <div className="grid grid-cols-4 gap-3">
                    <div className="p-3 bg-[#030712] rounded-md border border-[#1E293B] text-center">
                      <div className="text-2xl font-bold text-[#0EA5E9]">{importPreview.summary.agents_count}</div>
                      <div className="text-xs text-[#64748B]">Agents</div>
                    </div>
                    <div className="p-3 bg-[#030712] rounded-md border border-[#1E293B] text-center">
                      <div className="text-2xl font-bold text-[#7C3AED]">{importPreview.summary.categories_count}</div>
                      <div className="text-xs text-[#64748B]">Categories</div>
                    </div>
                    <div className="p-3 bg-[#030712] rounded-md border border-[#1E293B] text-center">
                      <div className="text-2xl font-bold text-[#10B981]">{importPreview.summary.tasks_count}</div>
                      <div className="text-xs text-[#64748B]">Tasks</div>
                    </div>
                    <div className="p-3 bg-[#030712] rounded-md border border-[#1E293B] text-center">
                      <div className="text-2xl font-bold text-[#F59E0B]">{importPreview.summary.generations_count}</div>
                      <div className="text-xs text-[#64748B]">Generations</div>
                    </div>
                  </div>

                  {/* Agents with Categories and Tasks */}
                  {importPreview.agents && importPreview.agents.length > 0 && (
                    <div className="space-y-3">
                      <Label className="text-sm text-[#F1F5F9] font-medium">Agent Structure</Label>
                      <div className="space-y-3">
                        {importPreview.agents.map((agent, i) => (
                          <div key={i} className="p-3 bg-[#030712] rounded-md border border-[#1E293B]">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className="bg-[#0EA5E9]/20 text-[#0EA5E9] border-0">
                                {agent.name}
                              </Badge>
                              <span className="text-[10px] text-[#64748B]">{agent.description}</span>
                            </div>
                            {agent.categories && agent.categories.length > 0 && (
                              <div className="ml-4 space-y-2">
                                {agent.categories.map((cat, j) => (
                                  <div key={j} className="border-l-2 border-[#1E293B] pl-3">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs text-[#7C3AED] font-medium">{cat.name}</span>
                                      <span className="text-[10px] text-[#64748B]">({cat.tasks?.length || 0} tasks)</span>
                                    </div>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {cat.tasks?.map((task, k) => (
                                        <Badge key={k} variant="outline" className="text-[10px] py-0 px-1.5 text-[#10B981] border-[#10B981]/30">
                                          {task.name}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Workflows */}
                  {importPreview.workflows && importPreview.workflows.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-xs text-[#94A3B8]">Workflows Detected</Label>
                      <div className="flex flex-wrap gap-2">
                        {importPreview.workflows.slice(0, 8).map((workflow, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {workflow}
                          </Badge>
                        ))}
                        {importPreview.workflows.length > 8 && (
                          <Badge variant="outline" className="text-xs text-[#64748B]">
                            +{importPreview.workflows.length - 8} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Test Case Preview */}
                  {importPreview.test_case && (
                    <div className="space-y-2">
                      <Label className="text-xs text-[#94A3B8]">Test Case from Trace</Label>
                      <div className="p-3 bg-[#030712] rounded-md border border-[#1E293B] space-y-2">
                        <div>
                          <span className="text-[10px] text-[#64748B] block mb-1">Input:</span>
                          <pre className="text-xs text-[#F1F5F9] whitespace-pre-wrap max-h-20 overflow-auto">
                            {importPreview.test_case.input.substring(0, 300)}
                            {importPreview.test_case.input.length > 300 && '...'}
                          </pre>
                        </div>
                        <div>
                          <span className="text-[10px] text-[#64748B] block mb-1">Expected Output:</span>
                          <pre className="text-xs text-[#94A3B8] whitespace-pre-wrap max-h-20 overflow-auto">
                            {importPreview.test_case.expected_output.substring(0, 300)}
                            {importPreview.test_case.expected_output.length > 300 && '...'}
                          </pre>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Total Observations */}
                  <div className="text-xs text-[#64748B] text-center pt-2 border-t border-[#1E293B]">
                    Total observations in trace: {importPreview.summary.total_observations}
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setShowImportDialog(false);
                  setImportPreview(null);
                }}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleImport} 
                  disabled={importing}
                  className="bg-[#0EA5E9] hover:bg-[#0284C7]"
                  data-testid="confirm-import-btn"
                >
                  {importing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-1" />
                      Import
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {loading ? (
            <div className="text-center text-[#64748B] py-4">Loading...</div>
          ) : testCases.length === 0 ? (
            <div className="text-center text-[#64748B] py-8 text-sm">
              No test cases yet. Create one to test your prompt.
            </div>
          ) : (
            <div className="space-y-2">
              {testCases.map(tc => (
                <div key={tc.id} className="border border-[#1E293B] rounded-md overflow-hidden">
                  <div className="p-3 bg-[#030712] flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-[#F1F5F9]">{tc.name}</span>
                      <Badge variant="outline" className="text-[10px]">
                        {tc.inputs?.length || 0} inputs
                      </Badge>
                      {tc.assertions?.length > 0 && (
                        <Badge variant="outline" className="text-[10px]">
                          {tc.assertions.length} assertions
                        </Badge>
                      )}
                      {tc.expected_output && (
                        <Badge variant="outline" className="text-[10px] bg-[#0EA5E9]/10 text-[#0EA5E9] border-[#0EA5E9]/30">
                          Has Expected Output
                        </Badge>
                      )}
                      {tc.evaluation_method && (
                        <Badge variant="outline" className="text-[10px] bg-purple-500/10 text-purple-400 border-purple-500/30">
                          {EVALUATION_METHODS.find(m => m.value === tc.evaluation_method?.type)?.label || tc.evaluation_method?.type}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRunTestCase(tc)}
                        disabled={runningTest === tc.id}
                        className="h-7 px-2"
                        data-testid={`run-test-${tc.id}`}
                      >
                        {runningTest === tc.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditTestCase(tc)}
                        className="h-7 px-2"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteTestCase(tc.id)}
                        className="h-7 px-2 text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      {testResults[tc.id] && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setExpandedResults(prev => ({ ...prev, [tc.id]: !prev[tc.id] }))}
                          className="h-7 px-2"
                        >
                          {expandedResults[tc.id] ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Test Result */}
                  {testResults[tc.id] && expandedResults[tc.id] && (
                    <div className="p-3 border-t border-[#1E293B] space-y-3">
                      {/* Status */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {testResults[tc.id].success ? (
                            testResults[tc.id].all_passed ? (
                              <Badge className="bg-green-500/10 text-green-400 border-green-500/30">
                                <Check className="w-3 h-3 mr-1" />
                                All Passed
                              </Badge>
                            ) : (
                              <Badge className="bg-yellow-500/10 text-yellow-400 border-yellow-500/30">
                                Some Failed
                              </Badge>
                            )
                          ) : (
                            <Badge className="bg-red-500/10 text-red-400 border-red-500/30">
                              <X className="w-3 h-3 mr-1" />
                              Error
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-[10px] text-[#64748B]">
                          <Clock className="w-3 h-3" />
                          {testResults[tc.id].execution_time_ms}ms
                          {testResults[tc.id].token_count && (
                            <>
                              <span>•</span>
                              {testResults[tc.id].token_count} tokens
                            </>
                          )}
                        </div>
                      </div>

                      {/* Evaluation Result */}
                      {testResults[tc.id].evaluation_result && (
                        <div className="space-y-2 p-3 bg-[#1E293B]/30 rounded-md">
                          <div className="flex items-center justify-between">
                            <Label className="text-[10px] text-[#64748B]">Evaluation Result</Label>
                            <div className="flex items-center gap-2">
                              {testResults[tc.id].evaluation_result.passed ? (
                                <Badge className="bg-green-500/10 text-green-400 border-green-500/30">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Passed
                                </Badge>
                              ) : testResults[tc.id].evaluation_result.passed === false ? (
                                <Badge className="bg-red-500/10 text-red-400 border-red-500/30">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Failed
                                </Badge>
                              ) : (
                                <Badge className="bg-yellow-500/10 text-yellow-400 border-yellow-500/30">
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                  Pending
                                </Badge>
                              )}
                              <Badge variant="outline" className="text-[10px]">
                                {testResults[tc.id].evaluation_result.method}
                              </Badge>
                            </div>
                          </div>
                          {testResults[tc.id].evaluation_result.similarity !== null && (
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-[#64748B]">Similarity:</span>
                              <div className="flex-1 h-1.5 bg-[#1E293B] rounded-full overflow-hidden">
                                <div 
                                  className={cn(
                                    "h-full rounded-full transition-all",
                                    testResults[tc.id].evaluation_result.passed 
                                      ? "bg-green-500" 
                                      : "bg-red-500"
                                  )}
                                  style={{ width: `${(testResults[tc.id].evaluation_result.similarity || 0) * 100}%` }}
                                />
                              </div>
                              <span className="text-[10px] font-mono text-[#0EA5E9]">
                                {Math.round((testResults[tc.id].evaluation_result.similarity || 0) * 100)}%
                              </span>
                            </div>
                          )}
                          <p className="text-xs text-[#94A3B8]">{testResults[tc.id].evaluation_result.details}</p>
                        </div>
                      )}

                      {/* Expected vs Actual Comparison */}
                      {testResults[tc.id].expected_output && (
                        <div className="space-y-2">
                          <Label className="text-[10px] text-[#64748B]">Expected Output</Label>
                          <pre className="p-2 bg-[#0F172A] border border-[#1E293B] rounded-sm text-xs font-mono text-[#94A3B8] whitespace-pre-wrap max-h-32 overflow-auto">
                            {testResults[tc.id].expected_output}
                          </pre>
                        </div>
                      )}

                      {/* Assertions Results */}
                      {testResults[tc.id].assertions?.length > 0 && (
                        <div className="space-y-1">
                          <Label className="text-[10px] text-[#64748B]">Assertions</Label>
                          <div className="space-y-1">
                            {testResults[tc.id].assertions.map((a, i) => (
                              <div key={i} className="flex items-center gap-2 text-xs">
                                {a.passed ? (
                                  <Check className="w-3 h-3 text-green-400" />
                                ) : (
                                  <X className="w-3 h-3 text-red-400" />
                                )}
                                <span className={a.passed ? 'text-[#94A3B8]' : 'text-red-400'}>
                                  {a.type}: "{a.value}"
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Output */}
                      {testResults[tc.id].output && (
                        <div className="space-y-1">
                          <Label className="text-[10px] text-[#64748B]">Actual Output</Label>
                          <pre className="p-2 bg-[#0F172A] border border-[#1E293B] rounded-sm text-xs font-mono text-[#F1F5F9] whitespace-pre-wrap max-h-40 overflow-auto">
                            {testResults[tc.id].output}
                          </pre>
                        </div>
                      )}

                      {/* Error */}
                      {testResults[tc.id].error && (
                        <div className="p-2 bg-red-500/10 border border-red-500/30 rounded-sm text-xs text-red-400">
                          {testResults[tc.id].error}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
    </div>
  );
}
